import { motion } from "framer-motion";
import { ABOUT } from "../lib/data";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function About() {
  const [primary] = ABOUT.images;

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-16 bg-[var(--bg-secondary)] border-y border-white/10 overflow-hidden"
    >
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-7">
          <motion.p
            variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="text-xs md:text-sm font-bold uppercase tracking-[0.25em] text-[var(--accent-primary)]"
          >
            04 — About Us
          </motion.p>
          <motion.h2
            variants={fadeUp} custom={1} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="mt-5 font-display text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.02] text-white"
          >
            Who we are
          </motion.h2>
          <motion.p
            variants={fadeUp} custom={2} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="mt-8 text-lg md:text-2xl text-zinc-300 leading-relaxed font-medium max-w-2xl"
          >
            {ABOUT.intro}
          </motion.p>

          <div className="mt-12">
            <span className="font-display text-sm font-semibold uppercase tracking-[0.2em] text-zinc-500">
              Our Story
            </span>
            {ABOUT.story.map((para, i) => (
              <motion.p
                key={i}
                variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
                className="mt-5 text-base md:text-lg text-zinc-400 leading-relaxed max-w-2xl"
              >
                {para}
              </motion.p>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          {/* Single hero image depicting the team */}
          <div className="relative">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="relative rounded-2xl overflow-hidden border border-white/10"
              data-testid="about-image-primary"
            >
              <img
                src={primary.src}
                alt={primary.alt}
                loading="lazy"
                className="w-full h-[400px] md:h-[500px] object-cover grayscale-[0.15]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-primary)]/70 via-transparent to-transparent" />
              {/* accent frame corner */}
              <div className="absolute top-4 left-4 h-10 w-10 border-t-2 border-l-2 border-[var(--accent-secondary)] rounded-tl-lg" />
            </motion.div>

            {/* floating stat badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="absolute -top-6 -right-3 md:-right-5 glass rounded-2xl px-5 py-3 text-center"
            >
              <div className="font-display text-2xl md:text-3xl font-black text-gradient-brand leading-none">
                2017
              </div>
              <div className="mt-1 text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                Founded
              </div>
            </motion.div>
          </div>

          <motion.blockquote
            variants={fadeUp} custom={1} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="mt-8 rounded-2xl border border-[var(--accent-primary)]/30 bg-[var(--accent-primary)]/5 p-7"
            data-testid="about-pullquote"
          >
            <p className="font-display text-xl md:text-2xl font-medium leading-snug text-white">
              &ldquo;{ABOUT.quote}&rdquo;
            </p>
          </motion.blockquote>

          <motion.div
            variants={fadeUp} custom={2} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="mt-8 rounded-2xl border border-white/10 p-7"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--accent-secondary)]">
              Our Vision
            </span>
            <p className="mt-3 text-base md:text-lg text-zinc-300 leading-relaxed">
              {ABOUT.vision}
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
